import axios from "axios";
import { API_URL } from "../config";
import { getOfflineValue, setOfflineValue, removeOfflineValue } from "./offlineIndexedDB";

const STORAGE_KEYS = {
  salesQueue: "sales_queue",
  salesProducts: "sales_products",
  salesBrands: "sales_brands",
  salesUnits: "sales_units",
  salesBranches: "sales_branches",
  salesWarehouses: "sales_warehouses",
  salesSuppliers: "sales_suppliers",
  salesDesignations: "sales_designations",
  salesDepartments: "sales_departments",
  salesCustomersRetail: "sales_customers_retail",
  salesCustomersWholesale: "sales_customers_wholesale",
  salesEmployees: "sales_employees",
  salesFloors: "sales_floors",
  salesBankAccounts: "sales_bank_accounts",
  salesRecords: "sales_records",
  salesRecordDetails: "sales_record_details",
  productSearchProducts: "product_search_products",
  salesProductLocations: "sales_product_locations",
  salesProductStock: "sales_product_stock",
  salesAreas: "sales_areas",
  salesCategories: "sales_categories",
  salesUsers: "sales_users",
  salesIndividualProducts: "sales_individual_products",
  institutionProfile: "institution_profile",
};
const OFFLINE_SALES_SYNC_EVENT = "offline-sales-sync";
let activeSalesSyncPromise = null;

const normalizeProductLocationKey = (row = {}) => {
  const purchaseRate = Number(row?.prod_purchase_rate ?? row?.average_purchase_rate ?? 0);
  return [
    row?.prod_id ?? "",
    row?.prod_floor_id ?? row?.floor_id ?? "",
    row?.prod_rack_id ?? row?.rack_id ?? "",
    row?.prod_floor_name ?? row?.floor_name ?? "",
    row?.prod_rack_name ?? row?.rack_name ?? "",
    purchaseRate.toFixed(4),
  ].join("||");
};

const mergeProductLocationRows = (existingRows, incomingRows) => {
  const grouped = new Map();

  (Array.isArray(existingRows) ? existingRows : []).forEach((row) => {
    grouped.set(normalizeProductLocationKey(row), { ...row });
  });

  (Array.isArray(incomingRows) ? incomingRows : []).forEach((row) => {
    const currentQty = Number(row?.prod_qty ?? row?.current_quantity ?? row?.current_stock ?? row?.stock_qty ?? 0);
    grouped.set(normalizeProductLocationKey(row), {
      ...row,
      prod_qty: Number.isFinite(currentQty) ? currentQty : 0,
    });
  });

  return Array.from(grouped.values());
};

const emitOfflineSalesSyncEvent = (detail = {}) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(OFFLINE_SALES_SYNC_EVENT, {
      detail: {
        queued: 0,
        synced: 0,
        status: "idle",
        ...detail,
      },
    })
  );
};

const readQueue = async () => {
  const queue = await getOfflineValue(STORAGE_KEYS.salesQueue, []);
  return Array.isArray(queue) ? queue : [];
};

const writeQueue = async (queue) => {
  await setOfflineValue(STORAGE_KEYS.salesQueue, Array.isArray(queue) ? queue : []);
  return queue;
};

const enqueueOfflineSale = async (payload) => {
  const queue = await readQueue();
  const nextItem = {
    id: `offline-sale-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    status: "pending",
    createdAt: new Date().toISOString(),
    ...payload,
  };

  queue.push(nextItem);
  await writeQueue(queue);
  return nextItem;
};

const removeQueueItem = async (queueItemId) => {
  const queue = (await readQueue()).filter((item) => item.id !== queueItemId);
  await writeQueue(queue);
  return queue;
};

const updateQueueItem = async (queueItemId, nextValue) => {
  const queue = (await readQueue()).map((item) =>
    item.id === queueItemId ? { ...item, ...nextValue } : item
  );
  await writeQueue(queue);
  return queue;
};

const getOfflineQueueCount = async () => (await readQueue()).length;

const syncOfflineSalesQueue = async (authInfo, options = {}) => {
  if (activeSalesSyncPromise) return activeSalesSyncPromise;

  activeSalesSyncPromise = (async () => {
    const pendingBefore = await readQueue();
    const token = authInfo?.token;

    if (
      !token ||
      typeof navigator === "undefined" ||
      navigator.onLine === false ||
      pendingBefore.length === 0
    ) {
      emitOfflineSalesSyncEvent({
        status: pendingBefore.length > 0 ? "pending" : "idle",
        queued: pendingBefore.length,
        synced: 0,
      });
      return {
        syncedCount: 0,
        queuedCount: pendingBefore.length,
        syncedItems: [],
      };
    }

    emitOfflineSalesSyncEvent({
      status: "syncing",
      queued: pendingBefore.length,
      synced: 0,
    });

    let syncedCount = 0;
    const syncedItems = [];
    let queueSnapshot = pendingBefore;

    for (const item of queueSnapshot) {
      if (!item?.sale || !item?.cart) continue;

      try {
        await axios.post(
          `${API_URL}${item.endpoint || "/api/save-sale"}`,
          { cart: item.cart, sale: item.sale },
          { headers: { "auth-token": token } }
        );

        await removeQueueItem(item.id);
        syncedItems.push(item);
        syncedCount += 1;

        if (typeof options.onItemSynced === "function") {
          await options.onItemSynced(item);
        }

        if (typeof window !== "undefined") {
          const productIds = (Array.isArray(item.cart) ? item.cart : [])
            .map((cartItem) => Number(cartItem?.prod_id || 0))
            .filter(Boolean);

          if (productIds.length > 0) {
            window.dispatchEvent(
              new CustomEvent("sales-stock-updated", {
                detail: {
                  productIds,
                  at: Date.now(),
                },
              })
            );
          }
        }

        queueSnapshot = await readQueue();
        emitOfflineSalesSyncEvent({
          status: "syncing",
          queued: queueSnapshot.length,
          synced: syncedCount,
          lastSyncedAt: new Date().toISOString(),
        });
      } catch (error) {
        emitOfflineSalesSyncEvent({
          status: "error",
          queued: queueSnapshot.length,
          synced: syncedCount,
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Offline sale sync failed.",
        });
        throw error;
      }
    }

    const queuedCount = await getOfflineQueueCount();
    emitOfflineSalesSyncEvent({
      status: queuedCount > 0 ? "pending" : "completed",
      queued: queuedCount,
      synced: syncedCount,
      lastSyncedAt: new Date().toISOString(),
    });

    return { syncedCount, queuedCount, syncedItems };
  })().finally(() => {
    activeSalesSyncPromise = null;
  });

  return activeSalesSyncPromise;
};

const cacheSalesMasterData = async (type, value) => {
  if (type === "products") {
    await setOfflineValue(STORAGE_KEYS.salesProducts, value);
    await setOfflineValue(STORAGE_KEYS.productSearchProducts, value);
    return value;
  }

  if (type === "customers-retail") {
    await setOfflineValue(STORAGE_KEYS.salesCustomersRetail, value);
    return value;
  }

  if (type === "customers-wholesale") {
    await setOfflineValue(STORAGE_KEYS.salesCustomersWholesale, value);
    return value;
  }

  if (type === "brands") {
    await setOfflineValue(STORAGE_KEYS.salesBrands, value);
    return value;
  }

  if (type === "units") {
    await setOfflineValue(STORAGE_KEYS.salesUnits, value);
    return value;
  }

  if (type === "branches") {
    await setOfflineValue(STORAGE_KEYS.salesBranches, value);
    return value;
  }

  if (type === "warehouses") {
    await setOfflineValue(STORAGE_KEYS.salesWarehouses, value);
    return value;
  }

  if (type === "suppliers") {
    await setOfflineValue(STORAGE_KEYS.salesSuppliers, value);
    return value;
  }

  if (type === "designations") {
    await setOfflineValue(STORAGE_KEYS.salesDesignations, value);
    return value;
  }

  if (type === "departments") {
    await setOfflineValue(STORAGE_KEYS.salesDepartments, value);
    return value;
  }

  if (type === "employees") {
    await setOfflineValue(STORAGE_KEYS.salesEmployees, value);
    return value;
  }

  if (type === "floors") {
    await setOfflineValue(STORAGE_KEYS.salesFloors, value);
    return value;
  }

  if (type === "bank-accounts") {
    await setOfflineValue(STORAGE_KEYS.salesBankAccounts, value);
    return value;
  }

  if (type === "areas") {
    await setOfflineValue(STORAGE_KEYS.salesAreas, value);
    return value;
  }

  if (type === "categories") {
    await setOfflineValue(STORAGE_KEYS.salesCategories, value);
    return value;
  }

  if (type === "users") {
    await setOfflineValue(STORAGE_KEYS.salesUsers, value);
    return value;
  }

  if (type === "individual-products") {
    await setOfflineValue(STORAGE_KEYS.salesIndividualProducts, value);
  }

  if (type === "product-locations") {
    const existingValue = await getOfflineValue(STORAGE_KEYS.salesProductLocations, []);
    const mergedValue = mergeProductLocationRows(existingValue, value);
    await setOfflineValue(STORAGE_KEYS.salesProductLocations, mergedValue);
    return mergedValue;
  }

  if (type === "product-stock") {
    await setOfflineValue(STORAGE_KEYS.salesProductStock, value);
    return value;
  }

  if (type === "institution-profile") {
    await setOfflineValue(STORAGE_KEYS.institutionProfile, value);
    return value;
  }

  return value;
};

const loadSalesMasterData = async (type, fallback = []) => {
  if (type === "products") return getOfflineValue(STORAGE_KEYS.salesProducts, fallback);
  if (type === "customers-retail") return getOfflineValue(STORAGE_KEYS.salesCustomersRetail, fallback);
  if (type === "customers-wholesale") return getOfflineValue(STORAGE_KEYS.salesCustomersWholesale, fallback);
  if (type === "brands") return getOfflineValue(STORAGE_KEYS.salesBrands, fallback);
  if (type === "units") return getOfflineValue(STORAGE_KEYS.salesUnits, fallback);
  if (type === "branches") return getOfflineValue(STORAGE_KEYS.salesBranches, fallback);
  if (type === "warehouses") return getOfflineValue(STORAGE_KEYS.salesWarehouses, fallback);
  if (type === "suppliers") return getOfflineValue(STORAGE_KEYS.salesSuppliers, fallback);
  if (type === "designations") return getOfflineValue(STORAGE_KEYS.salesDesignations, fallback);
  if (type === "departments") return getOfflineValue(STORAGE_KEYS.salesDepartments, fallback);
  if (type === "employees") return getOfflineValue(STORAGE_KEYS.salesEmployees, fallback);
  if (type === "floors") return getOfflineValue(STORAGE_KEYS.salesFloors, fallback);
  if (type === "bank-accounts") return getOfflineValue(STORAGE_KEYS.salesBankAccounts, fallback);
  if (type === "areas") return getOfflineValue(STORAGE_KEYS.salesAreas, fallback);
  if (type === "categories") return getOfflineValue(STORAGE_KEYS.salesCategories, fallback);
  if (type === "users") return getOfflineValue(STORAGE_KEYS.salesUsers, fallback);
  if (type === "individual-products") return getOfflineValue(STORAGE_KEYS.salesIndividualProducts, fallback);
  if (type === "product-locations") return getOfflineValue(STORAGE_KEYS.salesProductLocations, fallback);
  if (type === "product-stock") return getOfflineValue(STORAGE_KEYS.salesProductStock, fallback);
  if (type === "institution-profile") return getOfflineValue(STORAGE_KEYS.institutionProfile, fallback);
  return fallback;
};

const cacheSalesRecordResult = async (cacheKey, value) => {
  const cache = (await getOfflineValue(STORAGE_KEYS.salesRecords, {})) || {};
  cache[cacheKey] = value;
  await setOfflineValue(STORAGE_KEYS.salesRecords, cache);
  return value;
};

const loadSalesRecordResult = async (cacheKey, fallback = []) => {
  const cache = (await getOfflineValue(STORAGE_KEYS.salesRecords, {})) || {};
  return cache?.[cacheKey] ?? fallback;
};

const buildSalesRecordCacheKey = (payload) => JSON.stringify(payload || {});

const clearOfflineData = async () => {
  await Promise.all(Object.values(STORAGE_KEYS).map((key) => removeOfflineValue(key)));
};

export {
  OFFLINE_SALES_SYNC_EVENT,
  STORAGE_KEYS,
  readQueue,
  writeQueue,
  enqueueOfflineSale,
  removeQueueItem,
  updateQueueItem,
  getOfflineQueueCount,
  syncOfflineSalesQueue,
  cacheSalesMasterData,
  loadSalesMasterData,
  cacheSalesRecordResult,
  loadSalesRecordResult,
  buildSalesRecordCacheKey,
  clearOfflineData,
};
